import React, { useEffect, useMemo, useState } from 'react';
import { tradingAPI } from '../../services/api';

/* ═══════════ Charts & tables: pasteable trade-changes table + SAA positioning ═══════════
   The changes table shows ONLY the funds that moved (new model vs old model per strategy)
   with asset-class band totals. The positioning table replicates the Portfolio Dashboard's
   SAA positioning rules exactly — tilts averaged across the selected strategies (DS 30/50/
   70/90 by default), 5-bucket dots, growth-vs-defensive summary row — but computed from
   THIS app's target weights, i.e. positioning as a result of the changes. Both tables copy
   to the clipboard as inline-styled HTML so they paste cleanly into Word / Outlook. */

const DEFAULT_CODES = ['DDP', 'DS30', 'DS50', 'DS70', 'DS90', 'DS100', 'DS100Plus'];

/* ── Positioning rules copied from PortfolioDashboardApp.jsx — keep in sync ── */
const GROWTH_ASSET_CLASSES = [
  'Australian Equities', 'International Equities',
  'Real Estate', 'Infrastructure', 'Global Alpha - G', 'Global Alpha',
];
const MERGE_ASSET_CLASSES = { 'EM Equities': 'International Equities' };
const STANDARD_THRESHOLD_ACS = new Set([
  'Australian Equities', 'International Equities',
  'Corporate Debt', 'Government Bonds',
  'Total Growth vs Defensive', 'Cash',
]);
const getPosition = (tilt, assetClass, exposure = null) => {
  const strongThreshold = STANDARD_THRESHOLD_ACS.has(assetClass) ? 5 : 3;
  // zero exposure against a positive SAA weight => strong underweight regardless of size
  if (exposure != null && exposure < 0.5 && (exposure - tilt) > 0.5) return 'strongUW';
  if (tilt > strongThreshold) return 'strongOW';
  if (tilt >= 0.5) return 'overweight';
  if (Math.abs(tilt) < 0.5) return 'neutral';
  if (tilt <= -strongThreshold) return 'strongUW';
  return 'underweight';
};
const POSITION_COLUMNS = ['strongUW', 'underweight', 'neutral', 'overweight', 'strongOW'];
const POSITION_LABELS = {
  strongUW: 'Strong Underweight', underweight: 'Underweight', neutral: 'Neutral',
  overweight: 'Overweight', strongOW: 'Strong Overweight',
};
const POSITION_DOT_COLORS = {
  strongUW: '#8B0000', underweight: '#dc3545', neutral: '#072040',
  overweight: '#91CF9B', strongOW: '#2d8a4e',
};
const POSITIONING_ASSET_CLASSES = [
  'Australian Equities', 'International Equities',
  'Real Estate', 'Infrastructure', 'Global Alpha - G',
  'Cash', 'Government Bonds', 'Corporate Debt',
];

const UP = '#2d8a4e', DOWN = '#dc3545', NAVY = '#072040', BAND = '#E7E6E6', BORDER = '#d9d9d9';
const TOL = 0.005;

const fmtD = (v, dp = 2) => `${v > 0 ? '+' : ''}${Number(v).toFixed(dp).replace(/\.?0+$/, '')}%`;

function DeltaCell({ d }) {
  if (d == null || Math.abs(d) <= TOL) return <span className="chg-flat">—</span>;
  return <span className={`chg-delta ${d > 0 ? 'up' : 'down'}`}>{fmtD(d)}</span>;
}

/* Net direction of a group across the portfolio columns: ▲ when every mover is up,
   ▼ when every mover is down, nothing when the signals are mixed (or all flat). */
function netArrow(total) {
  const ds = Object.values(total || {}).filter((d) => d != null && Math.abs(d) > TOL);
  if (!ds.length) return null;
  const up = ds.some((d) => d > 0);
  const down = ds.some((d) => d < 0);
  return up && down ? null : (up ? 'up' : 'down');
}

/* Small diverging bar for the asset-class change chart (self-contained twin of TiltBar). */
function ChgBar({ value, max, color }) {
  const v = Number(value) || 0;
  const w = Math.min(50, (Math.abs(v) / (max || 1)) * 50);
  const near = Math.abs(v) <= TOL;
  return (
    <div className="tiltbar" title={`${fmtD(v)} change`}>
      <span className="tiltbar-track">
        {!near && (
          <span className="tiltbar-fill"
            style={{ background: color, ...(v >= 0 ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }) }} />
        )}
      </span>
      <span className={`tiltbar-num ${near ? 'flat' : ''}`}>{near ? '·' : fmtD(v, 1).replace('%', '')}</span>
    </div>
  );
}

/* Copy inline-styled HTML (+ plain-text fallback) to the clipboard so it pastes into
   Word / Outlook with formatting intact. Exported for the Mandate checks panel. */
export async function copyHtml(html, plain) {
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      })]);
      return true;
    }
  } catch (e) { /* fall through to execCommand */ }
  const el = document.createElement('div');
  el.contentEditable = 'true';
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  el.innerHTML = html;
  document.body.appendChild(el);
  const range = document.createRange();
  range.selectNodeContents(el);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  const ok = document.execCommand('copy');
  sel.removeAllRanges();
  document.body.removeChild(el);
  return ok;
}

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/* Export an inline-styled HTML fragment as a PNG: parse it into the DOM (measuring its
   rendered size on the way through), let XMLSerializer produce guaranteed well-formed
   XHTML, wrap in an SVG foreignObject, rasterise at 2× and download. Everything is
   inline / same-document, so the canvas stays untainted. */
async function exportPng(html, filename) {
  // Parse + measure off-screen; serialise from the DOM so the XML is always valid.
  const holder = document.createElement('div');
  holder.setAttribute('style', 'display:inline-block;background:#ffffff;padding:16px;font-family:Calibri,Arial,sans-serif;');
  holder.innerHTML = html;
  // Drop the APIR column from the changes-table PNG (kept in the clipboard/HTML copy).
  holder.querySelectorAll('.apir-col').forEach((el) => el.remove());
  // For the PNG only (on-screen tables are unchanged): enlarge every font by 50% while
  // tightening the cell padding and the chart's fixed bar widths, so the table keeps roughly
  // the same footprint and the larger text clearly dominates rather than the whole image
  // simply scaling up. Runs before the measure below so the SVG canvas fits the result.
  const FONT_SCALE = 1.5;   // text up
  const PAD_SCALE = 0.65;   // whitespace in
  const GFX_SCALE = 0.8;    // bar graphics narrower
  const scalePx = (val, factor) =>
    val.replace(/(-?[\d.]+)px/g, (_, n) => `${(parseFloat(n) * factor).toFixed(2)}px`);
  holder.querySelectorAll('*').forEach((el) => {
    const s = el.style;
    if (!s) return;
    const fs = s.fontSize;
    if (fs) {
      const m = /^([\d.]+)(pt|px|em|rem)$/.exec(fs.trim());
      if (m) s.fontSize = `${(parseFloat(m[1]) * FONT_SCALE).toFixed(2)}${m[2]}`;
    }
    if (s.padding) s.padding = scalePx(s.padding, PAD_SCALE);
    else ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft']
      .forEach((p) => { if (s[p]) s[p] = scalePx(s[p], PAD_SCALE); });
    // Only the chart's diverging bars carry inline px width/height — shrink them so the
    // enlarged number labels lead the eye.
    if (s.width) s.width = scalePx(s.width, GFX_SCALE);
    if (s.height) s.height = scalePx(s.height, GFX_SCALE);
  });
  // Wrap only the portfolio-name headers (the main width driver); keep fund / asset-class
  // names on a single line so they read cleanly.
  holder.querySelectorAll('th').forEach((el) => {
    el.style.whiteSpace = 'normal';
    el.style.overflowWrap = 'break-word';
    el.style.maxWidth = '150px';   // caps wide portfolio-name headers; short labels stay narrow
  });
  holder.querySelectorAll('tr > td:first-child').forEach((el) => {
    el.style.whiteSpace = 'nowrap';
  });
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;left:-9999px;top:0;';
  probe.appendChild(holder);
  document.body.appendChild(probe);
  const w = Math.ceil(holder.offsetWidth);
  const h = Math.ceil(holder.offsetHeight);
  const xhtml = new XMLSerializer().serializeToString(holder);
  document.body.removeChild(probe);
  if (!w || !h) throw new Error('Nothing to export — the table rendered empty');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">`
    + `<foreignObject width="100%" height="100%">${xhtml}</foreignObject></svg>`;
  const img = new Image();
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = () => rej(new Error('Could not render the table to an image'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);
  ctx.drawImage(img, 0, 0);
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
  if (!blob) throw new Error('PNG encoding failed');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);   // revoking immediately cancels the download
}

const stamp = () => new Date().toISOString().slice(0, 10);

export default function ChangesReport({ suites, lockedCodes }) {
  // Embedded mode: when lockedCodes is supplied (e.g. the portfolios visible in the
  // trading grid above), the mix is driven by the caller and the picker is hidden.
  const locked = Array.isArray(lockedCodes);
  const available = useMemo(() => {
    const seen = new Set();
    const out = [];
    (suites || []).forEach((s) => (s.portfolios || []).forEach((p) => {
      if (p.blender_code && !seen.has(p.blender_code)) {
        seen.add(p.blender_code);
        out.push({ bc: p.blender_code, name: p.display_name || p.blender_code, suite: s.name });
      }
    }));
    return out;
  }, [suites]);
  const nameOf = useMemo(() => Object.fromEntries(available.map((a) => [a.bc, a.name])), [available]);

  const [codesState, setCodesState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('trading.report.codes') || 'null');
      if (Array.isArray(saved) && saved.length) return saved;
    } catch (e) { /* ignore */ }
    return DEFAULT_CODES;
  });
  // In embedded mode the caller owns the mix; otherwise it's the user's saved/default set.
  const codes = locked ? lockedCodes : codesState;
  const setCodes = locked ? () => {} : setCodesState;
  const [basis, setBasis] = useState(() => localStorage.getItem('trading.report.basis') || 'approved');
  const [approvedOffset, setApprovedOffset] = useState(() => Number(localStorage.getItem('trading.report.approvedOffset')) || 1);
  const [saaYear, setSaaYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(null);   // 'changes' | 'positioning'
  const [posExcluded, setPosExcluded] = useState(() => new Set());   // portfolios dropped from the positioning average
  const togglePos = (bc) => setPosExcluded((prev) => {
    const n = new Set(prev);
    if (n.has(bc)) n.delete(bc); else n.add(bc);
    return n;
  });
  // White-label brands and their renamed portfolios, and per-column label overrides so a
  // column added via a brand shows that brand's name instead of the default.
  const [brands, setBrands] = useState([]);
  const [labelOverrides, setLabelOverrides] = useState({});   // blender_code → branded name

  useEffect(() => {
    tradingAPI.getBrandedPortfolios().then((d) => setBrands(d.brands || [])).catch(() => setBrands([]));
  }, []);

  // Display label for a column: brand override → registry name → code.
  const labelFor = (bc) => labelOverrides[bc] || nameOf[bc] || bc;

  useEffect(() => { if (!locked) localStorage.setItem('trading.report.codes', JSON.stringify(codes)); }, [codes, locked]);
  useEffect(() => { localStorage.setItem('trading.report.basis', basis); }, [basis]);
  useEffect(() => { localStorage.setItem('trading.report.approvedOffset', String(approvedOffset)); }, [approvedOffset]);

  useEffect(() => {
    if (!codes.length) { setData(null); return; }
    let stale = false;
    setLoading(true);
    setErr(null);
    tradingAPI.changesReport(codes, basis, saaYear, approvedOffset)
      .then((d) => {
        if (stale) return;
        setData(d);
        // Keep the offset within the depth actually available for the current mix.
        if (basis === 'approved' && approvedOffset > Math.max(1, d.max_priors || 0)) setApprovedOffset(1);
      })
      .catch((e) => { if (!stale) setErr(e.response?.data?.detail || String(e)); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [codes.join(','), basis, saaYear, approvedOffset]);   // eslint-disable-line react-hooks/exhaustive-deps

  const strategies = data?.strategies || [];
  const live = strategies.filter((s) => !s.no_data);
  const groupOrder = data?.group_order || {};
  const gRank = (g) => (groupOrder[g] != null ? groupOrder[g] : 999);

  /* ── Changes table model: [{acg, total: {bc: d}, funds: [{key, name, deltas: {bc: d}}]}] ── */
  const changeGroups = useMemo(() => {
    if (!data) return [];
    const funds = data.funds || {};
    const keys = new Set();
    live.forEach((s) => Object.keys(s.changes || {}).forEach((k) => keys.add(k)));
    const byAcg = {};
    keys.forEach((k) => {
      const meta = funds[k] || { name: k, acg: 'Unclassified', sort_order: 0 };
      (byAcg[meta.acg] = byAcg[meta.acg] || []).push({
        key: k, name: meta.name || k, apir: meta.apir || null, sort_order: meta.sort_order || 0,
        deltas: Object.fromEntries(live.map((s) => [s.blender_code, s.changes?.[k]?.delta])),
        spans: Object.fromEntries(live.map((s) => {
          const c = s.changes?.[k];
          return [s.blender_code, c ? `${c.old.toFixed(2)}% → ${c.new.toFixed(2)}%` : null];
        })),
      });
    });
    // asset-class band totals (from the full rollup, so offsetting fund moves still net out)
    const acgs = new Set(Object.keys(byAcg));
    live.forEach((s) => Object.entries(s.acg || {}).forEach(([g, v]) => {
      if (Math.abs(v.delta) > TOL) acgs.add(g);
    }));
    return Array.from(acgs).sort((a, b) => (gRank(a) - gRank(b)) || a.localeCompare(b)).map((g) => ({
      acg: g,
      total: Object.fromEntries(live.map((s) => [s.blender_code, s.acg?.[g]?.delta ?? 0])),
      funds: (byAcg[g] || []).sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name)),
    }));
  }, [data]);   // eslint-disable-line react-hooks/exhaustive-deps

  const hasChanges = changeGroups.some((g) => g.funds.length
    || Object.values(g.total).some((d) => Math.abs(d) > TOL));

  /* ── Positioning model: dashboard rules on the AVERAGE tilt across selected strategies ── */
  const positioning = useMemo(() => {
    const withSaa = live.filter((s) => s.has_saa && !posExcluded.has(s.blender_code));
    if (!withSaa.length) return { rows: [], summary: null, included: [] };
    const merged = withSaa.map((s) => {
      const tilts = {}, exposures = {};
      Object.entries(s.tilts || {}).forEach(([ac, t]) => {
        const key = MERGE_ASSET_CLASSES[ac] || ac;
        tilts[key] = (tilts[key] || 0) + t;
      });
      Object.entries(s.exposures || {}).forEach(([ac, w]) => {
        const key = MERGE_ASSET_CLASSES[ac] || ac;
        exposures[key] = (exposures[key] || 0) + w;
      });
      return { bc: s.blender_code, tilts, exposures };
    });
    const anyHas = (ac) => merged.some((m) => m.tilts[ac] !== undefined);
    // use the split Global Alpha form when any strategy carries it, else the merged form
    const rowList = POSITIONING_ASSET_CLASSES.map((ac) => (
      ac === 'Global Alpha - G' && !anyHas(ac) && anyHas('Global Alpha') ? 'Global Alpha' : ac
    ));
    const rows = rowList.filter(anyHas).map((ac) => {
      const vals = merged.filter((m) => m.tilts[ac] !== undefined);
      const tilt = vals.reduce((sum, m) => sum + m.tilts[ac], 0) / vals.length;
      const exposure = vals.reduce((sum, m) => sum + (m.exposures[ac] || 0), 0) / vals.length;
      return { assetClass: ac, tilt, position: getPosition(tilt, ac, exposure) };
    });
    const hasSplitGA = rows.some((r) => r.assetClass === 'Global Alpha - G');
    const growthTilt = rows
      .filter((r) => GROWTH_ASSET_CLASSES.includes(r.assetClass)
        && !(hasSplitGA && r.assetClass === 'Global Alpha'))
      .reduce((sum, r) => sum + r.tilt, 0);
    const summary = rows.length ? {
      assetClass: 'Total Growth vs Defensive', tilt: growthTilt,
      position: getPosition(growthTilt, 'Total Growth vs Defensive'),
    } : null;
    return { rows, summary, included: withSaa.map((s) => s.blender_code) };
  }, [data, posExcluded]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Clipboard builders (inline-styled HTML — Word/Outlook-safe) ── */
  const td = (extra = '') => `border:1px solid ${BORDER};padding:4px 10px;font-family:Calibri,Arial,sans-serif;font-size:10.5pt;${extra}`;
  const deltaHtml = (d) => {
    if (d == null || Math.abs(d) <= TOL) return `<span style="color:#adb5bd;">&#8212;</span>`;
    const c = d > 0 ? UP : DOWN;
    return `<span style="color:${c};font-weight:600;white-space:nowrap;">${esc(fmtD(d))}</span>`;
  };
  const arrowHtml = (dir) => (dir
    ? `<span style="color:${dir === 'up' ? UP : DOWN};font-size:12pt;">${dir === 'up' ? '&#9650;' : '&#9660;'}</span>`
    : '');

  const buildChangesHtml = () => {
    let h = `<table style="border-collapse:collapse;">`;
    h += `<tr><th style="${td(`background:${NAVY};color:#fff;text-align:left;`)}">Fund</th>`
      + `<th class="apir-col" style="${td(`background:${NAVY};color:#fff;text-align:left;`)}">APIR</th>`
      + `<th style="${td(`background:${NAVY};color:#fff;text-align:center;`)}">Change</th>`;
    live.forEach((s) => {
      h += `<th style="${td(`background:${NAVY};color:#fff;text-align:center;`)}">${esc(labelFor(s.blender_code))}</th>`;
    });
    h += `</tr>`;
    changeGroups.forEach((g) => {
      h += `<tr><td style="${td(`background:${BAND};color:${NAVY};font-weight:700;`)}">${esc(g.acg)}</td>`
        + `<td class="apir-col" style="${td(`background:${BAND};`)}"></td>`
        + `<td style="${td(`background:${BAND};text-align:center;`)}">${arrowHtml(netArrow(g.total))}</td>`;
      live.forEach((s) => {
        h += `<td style="${td(`background:${BAND};text-align:center;font-weight:700;`)}">${deltaHtml(g.total[s.blender_code])}</td>`;
      });
      h += `</tr>`;
      g.funds.forEach((f) => {
        h += `<tr><td style="${td('padding-left:22px;')}">${esc(f.name)}</td>`
          + `<td class="apir-col" style="${td(`font-family:Consolas,monospace;font-size:9pt;color:#6c757d;white-space:nowrap;`)}">${esc(f.apir || '')}</td>`
          + `<td style="${td('')}"></td>`;
        live.forEach((s) => { h += `<td style="${td('text-align:center;')}">${deltaHtml(f.deltas[s.blender_code])}</td>`; });
        h += `</tr>`;
      });
    });
    h += `</table>`;
    let p = `Fund\tAPIR\tChange\t${live.map((s) => labelFor(s.blender_code)).join('\t')}\n`;
    changeGroups.forEach((g) => {
      const dir = netArrow(g.total);
      p += `${g.acg}\t\t${dir === 'up' ? '▲' : dir === 'down' ? '▼' : ''}\t${live.map((s) => (Math.abs(g.total[s.blender_code] || 0) > TOL ? fmtD(g.total[s.blender_code]) : '—')).join('\t')}\n`;
      g.funds.forEach((f) => {
        p += `  ${f.name}\t${f.apir || ''}\t\t${live.map((s) => (f.deltas[s.blender_code] != null ? fmtD(f.deltas[s.blender_code]) : '—')).join('\t')}\n`;
      });
    });
    return [h, p];
  };

  /* Chart as inline-styled XHTML (for the PNG exporter): diverging bar per ACG × portfolio. */
  const buildChartHtml = () => {
    const barCell = (v) => {
      const near = v == null || Math.abs(v) <= TOL;
      const w = Math.min(60, (Math.abs(v || 0) / maxAcgDelta) * 60);
      const fill = near ? '' : `<div style="position:absolute;top:1px;bottom:1px;${(v || 0) >= 0
        ? `left:50%;width:${w}px;` : `right:50%;width:${w}px;`}background:${(v || 0) >= 0 ? '#599BE3' : '#f0a500'};border-radius:2px;"></div>`;
      const num = near ? '·' : fmtD(v, 1).replace('%', '');
      return `<td style="${td('')}"><div style="display:flex;align-items:center;gap:6px;">`
        + `<div style="position:relative;width:124px;height:12px;">`
        + `<div style="position:absolute;left:50%;top:0;bottom:0;width:1px;background:#c7ced6;"></div>${fill}</div>`
        + `<span style="font-size:8.5pt;font-weight:600;color:${near ? '#b8c0c9' : '#212529'};white-space:nowrap;">${esc(num)}</span></div></td>`;
    };
    let h = `<table style="border-collapse:collapse;">`;
    h += `<tr><th style="${td(`background:${NAVY};color:#fff;text-align:left;`)}">Asset class changes</th>`;
    live.forEach((s) => {
      h += `<th style="${td(`background:${NAVY};color:#fff;text-align:center;`)}">${esc(labelFor(s.blender_code))}</th>`;
    });
    h += `</tr>`;
    changeGroups.forEach((g) => {
      h += `<tr><td style="${td(`color:${NAVY};font-weight:600;`)}">${esc(g.acg)}</td>`;
      live.forEach((s) => { h += barCell(g.total[s.blender_code]); });
      h += `</tr>`;
    });
    h += `</table>`;
    return h;
  };

  const buildPositioningHtml = () => {
    const all = [...positioning.rows, ...(positioning.summary ? [positioning.summary] : [])];
    let h = `<table style="border-collapse:collapse;">`;
    h += `<tr><th style="${td(`background:${NAVY};color:#fff;text-align:left;`)}">Asset class positioning</th>`;
    POSITION_COLUMNS.forEach((c) => {
      h += `<th style="${td(`background:${NAVY};color:#fff;text-align:center;font-size:9pt;`)}">${esc(POSITION_LABELS[c])}</th>`;
    });
    h += `</tr>`;
    all.forEach((r) => {
      const summary = r.assetClass === 'Total Growth vs Defensive';
      const label = summary ? `border-top:2px solid ${NAVY};font-weight:700;color:${NAVY};` : '';
      h += `<tr><td style="${td(label)}">${esc(r.assetClass)}</td>`;
      POSITION_COLUMNS.forEach((c) => {
        const on = r.position === c;
        h += `<td style="${td(`text-align:center;${summary ? `border-top:2px solid ${NAVY};` : ''}`)}">`
          + (on ? `<span style="color:${POSITION_DOT_COLORS[c]};font-size:14pt;">&#9679;</span>` : '') + `</td>`;
      });
      h += `</tr>`;
    });
    h += `</table>`;
    let p = `Asset class positioning\t${POSITION_COLUMNS.map((c) => POSITION_LABELS[c]).join('\t')}\n`;
    all.forEach((r) => {
      p += `${r.assetClass}\t${POSITION_COLUMNS.map((c) => (r.position === c ? '●' : '')).join('\t')}\n`;
    });
    return [h, p];
  };

  const doCopy = async (which) => {
    const [h, p] = which === 'changes' ? buildChangesHtml() : buildPositioningHtml();
    const ok = await copyHtml(h, p);
    setCopied(ok ? which : null);
    if (ok) setTimeout(() => setCopied(null), 1800);
  };

  const doExport = async (html, filename) => {
    try {
      await exportPng(html, filename);
    } catch (e) {
      setErr(`PNG export failed: ${e.message || e}`);
    }
  };

  const clearLabel = (bc) => setLabelOverrides((prev) => {
    if (!(bc in prev)) return prev;
    const next = { ...prev }; delete next[bc]; return next;
  });
  const addCode = (bc) => { if (bc && !codes.includes(bc)) { setCodes([...codes, bc]); clearLabel(bc); } };
  const addSuite = (suiteName) => {
    const s = (suites || []).find((x) => x.name === suiteName);
    if (!s) return;
    const toAdd = [...new Set((s.portfolios || []).map((p) => p.blender_code)
      .filter((bc) => bc && !codes.includes(bc)))];
    if (toAdd.length) { setCodes([...codes, ...toAdd]); toAdd.forEach(clearLabel); }
  };
  const removeCode = (bc) => { setCodes(codes.filter((c) => c !== bc)); clearLabel(bc); };

  // Branded picker: add a portfolio (or a whole brand) using the brand's renamed labels.
  // Selecting an already-added column just relabels it to the brand's name.
  const addBrandPortfolio = (bc, customName) => {
    setCodes((prev) => (prev.includes(bc) ? prev : [...prev, bc]));
    setLabelOverrides((prev) => ({ ...prev, [bc]: customName }));
  };
  const addBrand = (domain) => {
    const b = brands.find((x) => x.domain === domain);
    if (!b) return;
    setCodes((prev) => [...new Set([...prev, ...b.portfolios.map((p) => p.blender_code)])]);
    setLabelOverrides((prev) => {
      const next = { ...prev };
      b.portfolios.forEach((p) => { next[p.blender_code] = p.custom_name; });
      return next;
    });
  };

  const maxAcgDelta = useMemo(() => Math.max(0.1, ...changeGroups.flatMap((g) => Object.values(g.total).map((d) => Math.abs(d || 0)))), [changeGroups]);

  return (
    <>
      <header className="trading-header">
        <div>
          <h2>Charts &amp; tables</h2>
          <div className="trading-header-sub">
            <span className="trading-muted">
              Pasteable trade-changes table and post-change SAA positioning (dashboard tilt rules,
              averaged across the selected portfolios).
            </span>
          </div>
        </div>
      </header>

      {/* ── Controls: portfolio mix + basis + SAA vintage ── */}
      <div className="chgrep-controls">
        {!locked && (
        <div className="chgrep-chips" title="The portfolios in the mix — changes show one column each; positioning averages their tilts">
          {codes.map((bc) => (
            <span key={bc} className="chgrep-chip">
              {labelFor(bc)}
              <button onClick={() => removeCode(bc)} title="Remove from the mix">✕</button>
            </span>
          ))}
          <select
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (v.startsWith('__suite__')) addSuite(v.slice('__suite__'.length));
              else addCode(v);
            }}
            title="Add a portfolio — or pick a suite header to add all of them"
          >
            <option value="">+ Add portfolio…</option>
            {(suites || []).map((s) => {
              const avail = (s.portfolios || []).filter((p) => p.blender_code && !codes.includes(p.blender_code));
              if (!avail.length) return null;
              return (
                <optgroup key={s.name} label={s.name}>
                  <option value={`__suite__${s.name}`}>＋ Add all in {s.name}</option>
                  {avail.map((p) => (
                    <option key={`${s.name}:${p.portfolio_code}`} value={p.blender_code}>
                      {p.display_name || p.blender_code} ({p.blender_code})
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
          {brands.length > 0 && (
            <select
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (v.startsWith('__brand__')) {
                  addBrand(v.slice('__brand__'.length));
                } else {
                  const sep = v.indexOf('::');
                  const domain = v.slice(0, sep);
                  const bc = v.slice(sep + 2);
                  const b = brands.find((x) => x.domain === domain);
                  const p = b && b.portfolios.find((x) => x.blender_code === bc);
                  if (p) addBrandPortfolio(bc, p.custom_name);
                }
              }}
              title="Add a portfolio using a brand's renamed label (from admin branding)"
            >
              <option value="">+ Branded suite…</option>
              {brands.map((b) => (
                <optgroup key={b.domain} label={b.company_name}>
                  <option value={`__brand__${b.domain}`}>＋ Add all in {b.company_name}</option>
                  {b.portfolios.map((p) => (
                    <option key={`${b.domain}:${p.blender_code}`} value={`${b.domain}::${p.blender_code}`}>
                      {p.custom_name} ({p.blender_code})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
          {JSON.stringify(codes) !== JSON.stringify(DEFAULT_CODES) && (
            <button className="chgrep-reset" onClick={() => setCodes(DEFAULT_CODES)}
              title="Back to the default mix: Dynamic + DS 30/50/70/90/100/100 Plus">↺ DS defaults</button>
          )}
        </div>
        )}
        <div className="chgrep-opts">
          <label title="Which pair of versions the changes compare">
            Basis
            <select value={basis} onChange={(e) => setBasis(e.target.value)}>
              <option value="approved">Approved (last vs prior)</option>
              <option value="draft">Draft vs approved</option>
            </select>
          </label>
          {basis === 'approved' && (data?.max_priors || 0) > 1 && (
            <label title="Which prior approved version to compare the current approved against">
              Compare vs
              <select value={approvedOffset} onChange={(e) => setApprovedOffset(Number(e.target.value))}>
                {Array.from({ length: data.max_priors }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n === 1 ? 'Prior approved (last)' : `${n} approvals back`}</option>
                ))}
              </select>
            </label>
          )}
          <label title="SAA vintage used for the positioning tilts">
            SAA year
            <select value={saaYear || data?.saa_year || ''} onChange={(e) => setSaaYear(Number(e.target.value) || null)}>
              {(data?.saa_years || (data?.saa_year ? [data.saa_year] : [])).map((y) => (
                <option key={y} value={y}>FY{y}{y === data?.saa_year && !saaYear ? ' (active)' : ''}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {err && <div className="trading-error">{err}</div>}
      {loading && <div className="trading-muted" style={{ padding: '12px 0' }}>Loading…</div>}

      {data && !loading && (
        <>
          {/* ── Portfolio changes table ── */}
          <div className="chgrep-card">
            <div className="chgrep-card-head">
              <h3>Portfolio changes</h3>
              <span className="trading-muted">
                {basis === 'approved' ? 'last approved vs the prior approved version' : 'live draft vs the last approved version'} — only funds that moved
              </span>
              <span className="chgrep-actions">
                <button className="trading-btn sm" onClick={() => doCopy('changes')}
                  title="Copy as a formatted table — paste straight into Word or Outlook">
                  {copied === 'changes' ? '✓ Copied' : '⧉ Copy table'}
                </button>
                <button className="trading-btn sm" onClick={() => doExport(buildChangesHtml()[0], `portfolio_changes_${stamp()}.png`)}
                  title="Download this table as a PNG image">⇓ PNG</button>
              </span>
            </div>
            {strategies.some((s) => s.no_data || s.note) && (
              <div className="chgrep-notes">
                {strategies.filter((s) => s.no_data || s.note).map((s) => (
                  <span key={s.blender_code} className="veh-newtag">{s.blender_code}: {s.note}</span>
                ))}
              </div>
            )}
            {!live.length ? (
              <div className="trading-muted">No data for the selected portfolios on this basis.</div>
            ) : !hasChanges ? (
              <div className="trading-muted">No changes between the compared versions.</div>
            ) : (
              <div className="chgrep-scroll">
                <table className="chgrep-table">
                  <thead>
                    <tr>
                      <th className="chg-name">Fund</th>
                      <th className="chg-apirh">APIR</th>
                      <th className="chg-arrowh" title="Net direction of the asset class across the selected portfolios — blank when mixed">Change</th>
                      {live.map((s) => (
                        <th key={s.blender_code} title={`${s.new_label} vs ${s.old_label}${s.as_at ? ` · as at ${s.as_at}` : ''}`}>
                          {labelFor(s.blender_code)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {changeGroups.map((g) => {
                      const dir = netArrow(g.total);
                      return (
                        <React.Fragment key={g.acg}>
                          <tr className="chg-band">
                            <td className="chg-name">{g.acg}</td>
                            <td className="chg-apir"></td>
                            <td className={`chg-arrow${dir ? ` ${dir}` : ''}`}
                              title={dir ? `Net ${dir === 'up' ? 'increase' : 'decrease'} across the selected portfolios` : 'Mixed — up in some portfolios, down in others'}>
                              {dir === 'up' ? '▲' : dir === 'down' ? '▼' : ''}
                            </td>
                            {live.map((s) => (
                              <td key={s.blender_code}><DeltaCell d={g.total[s.blender_code]} /></td>
                            ))}
                          </tr>
                          {g.funds.map((f) => (
                            <tr key={f.key}>
                              <td className="chg-name chg-fund">{f.name}</td>
                              <td className="chg-apir">{f.apir || ''}</td>
                              <td className="chg-arrow"></td>
                              {live.map((s) => (
                                <td key={s.blender_code} title={f.spans[s.blender_code] || undefined}>
                                  <DeltaCell d={f.deltas[s.blender_code]} />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Asset-class change chart ── */}
          {hasChanges && (
            <div className="chgrep-card">
              <div className="chgrep-card-head">
                <h3>Asset class changes — chart</h3>
                <span className="trading-muted">total change per asset class group (right = increased)</span>
                <span className="chgrep-actions">
                  <button className="trading-btn sm" onClick={() => doExport(buildChartHtml(), `asset_class_changes_${stamp()}.png`)}
                    title="Download this chart as a PNG image">⇓ PNG</button>
                </span>
              </div>
              <div className="chgrep-scroll">
                <table className="chgrep-table chgrep-chart">
                  <thead>
                    <tr>
                      <th className="chg-name"></th>
                      {live.map((s) => <th key={s.blender_code}>{labelFor(s.blender_code)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {changeGroups.map((g, i) => (
                      <tr key={g.acg}>
                        <td className="chg-name">{g.acg}</td>
                        {live.map((s) => (
                          <td key={s.blender_code}>
                            <ChgBar value={g.total[s.blender_code]} max={maxAcgDelta}
                              color={(g.total[s.blender_code] || 0) >= 0 ? '#599BE3' : '#f0a500'} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── SAA positioning dot table ── */}
          <div className="chgrep-card">
            <div className="chgrep-card-head">
              <h3>Asset class positioning</h3>
              <span className="trading-muted">
                {positioning.included.length
                  ? `average tilt vs FY${data.saa_year} SAA across ${positioning.included.join(', ')} — ${basis === 'draft' ? 'draft' : 'approved'} weights`
                  : ''}
              </span>
              {positioning.rows.length > 0 && (
                <span className="chgrep-actions">
                  <button className="trading-btn sm" onClick={() => doCopy('positioning')}
                    title="Copy as a formatted table — paste straight into Word or Outlook">
                    {copied === 'positioning' ? '✓ Copied' : '⧉ Copy table'}
                  </button>
                  <button className="trading-btn sm" onClick={() => doExport(buildPositioningHtml()[0], `saa_positioning_${stamp()}.png`)}
                    title="Download this table as a PNG image">⇓ PNG</button>
                </span>
              )}
            </div>
            {live.some((s) => s.has_saa) && (
              <div className="chgrep-posinc" title="Which portfolios feed the average tilt — untick to exclude one from the calculation">
                <span className="chgrep-posinc-lbl">Include in average:</span>
                {live.filter((s) => s.has_saa).map((s) => (
                  <label key={s.blender_code} className="chgrep-posinc-item">
                    <input type="checkbox" checked={!posExcluded.has(s.blender_code)} onChange={() => togglePos(s.blender_code)} />
                    {labelFor(s.blender_code)}
                  </label>
                ))}
              </div>
            )}
            {!positioning.rows.length ? (
              <div className="trading-muted">No SAA on record for the selected portfolios{data.saa_year ? ` in FY${data.saa_year}` : ''}.</div>
            ) : (
              <div className="chgrep-scroll">
                <table className="chgrep-table chgrep-dots">
                  <thead>
                    <tr>
                      <th className="chg-name">Asset class</th>
                      <th title="Average post-change tilt vs SAA across the selected portfolios">Avg tilt</th>
                      {POSITION_COLUMNS.map((c) => <th key={c}>{POSITION_LABELS[c]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {[...positioning.rows, ...(positioning.summary ? [positioning.summary] : [])].map((r) => (
                      <tr key={r.assetClass} className={r.assetClass === 'Total Growth vs Defensive' ? 'chg-summary' : ''}>
                        <td className="chg-name">{r.assetClass}</td>
                        <td className="chg-tilt">{fmtD(r.tilt, 1)}</td>
                        {POSITION_COLUMNS.map((c) => (
                          <td key={c} className="chg-dotcell">
                            {r.position === c && (
                              <span className="chg-dot" style={{ background: POSITION_DOT_COLORS[c] }}
                                title={`${r.assetClass}: ${POSITION_LABELS[c]} (${fmtD(r.tilt, 1)} vs SAA)`} />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="trading-muted chgrep-footnote">
              Buckets use the Portfolio Dashboard rules: |tilt| &lt; 0.5% neutral; ≥ 0.5% over/underweight;
              beyond ±5% (equities, bonds, cash, growth total) or ±3% (others) strong; zero exposure
              against an SAA weight reads as strong underweight. EM Equities folds into International Equities.
            </div>
          </div>
        </>
      )}
    </>
  );
}
